/**
 * Keeps checkout clickable and requires agreement before navigation.
 *
 * The cart drawer and the cart page both replace their inner HTML on every cart
 * update, so the checkbox and the button are recreated constantly. Listeners are
 * therefore delegated on `document`, and the state is re-applied after each
 * `cartUpdate` broadcast as well as on any DOM replacement.
 */
(() => {
  const STORAGE_KEY = 'cart:terms-accepted';
  const CHECKBOX_SELECTOR = '[data-terms-gate-checkbox]';
  const BUTTON_SELECTOR = '[data-terms-gate-button]';
  const CONSENT_ATTRIBUTE = 'Sözleşme onayı';
  let sessionAccepted;

  /** @returns {boolean} Whether the shopper has previously accepted the agreement. */
  const isAccepted = () => {
    if (sessionAccepted !== undefined) return sessionAccepted;
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch (error) {
      return false;
    }
  };

  /** @param {boolean} accepted */
  const persist = (accepted) => {
    sessionAccepted = accepted;
    try {
      if (accepted) {
        localStorage.setItem(STORAGE_KEY, 'true');
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      // Private browsing or blocked storage: the gate still works for this session.
    }
  };

  /**
   * Keeps one dialog per selector in the document, parented to <body>.
   *
   * The snippet renders the dialog inside the cart markup, whose ancestors carry a
   * `transform` — that creates a containing block which knocks the modal off centre,
   * so it has to live directly under <body>. Cart re-renders reinstate a fresh copy
   * in the markup each time, hence the sweep on every state pass.
   *
   * @returns {HTMLDialogElement | null}
   */
  const getDialog = (selector) => {
    const hoisted = document.body.querySelector(`:scope > ${selector}`);

    if (!hoisted) {
      const dialog = document.querySelector(selector);
      if (dialog) document.body.append(dialog);
      return dialog;
    }

    for (const stale of document.querySelectorAll(selector)) {
      if (stale !== hoisted) stale.remove();
    }

    return hoisted;
  };

  /** @returns {HTMLElement | null} The inline agreement panel tied to a checkout button. */
  const getPanel = (button) => {
    const id = button?.dataset.termsPanelId;
    return id ? document.getElementById(id) : null;
  };

  /**
   * Switches a tabbed panel (preliminary information form + agreement) to one text.
   * An unknown or missing name falls back to the first tab, the form, which the law
   * requires to be shown first. Panels without tabs are left as they are.
   */
  const selectTab = (panel, name) => {
    const tabs = [...panel.querySelectorAll('[data-terms-tab]')];
    if (!tabs.length) return;
    const active = tabs.find((tab) => tab.dataset.termsTab === name) ?? tabs[0];

    for (const tab of tabs) {
      const selected = tab === active;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    }
    for (const doc of panel.querySelectorAll('[data-terms-doc]')) {
      doc.hidden = doc.dataset.termsDoc !== active.dataset.termsTab;
    }
    return active;
  };

  /**
   * Shows the agreement in place of the checkbox. Until the shopper has consented, the
   * checkout button also turns into the accept action, so the next press both consents
   * and continues to payment; after consent the panel is only there to be re-read.
   */
  const openPanel = (panel, button, tab) => {
    selectTab(panel, tab);
    panel.classList.remove('is-closing');
    panel.hidden = false;
    panel.closest('[data-terms-gate]')?.classList.add('is-reviewing');
    if (!isAccepted()) {
      panel.dataset.termsAccepting = '';
      if (button.dataset.termsLabel === undefined) button.dataset.termsLabel = button.innerHTML;
      button.textContent = panel.dataset.confirmLabel;
    }
    panel.scrollIntoView({ block: 'nearest' });
  };

  const hidePanel = (panel) => {
    panel.classList.remove('is-closing');
    panel.hidden = true;
    delete panel.dataset.termsAccepting;
    panel.closest('[data-terms-gate]')?.classList.remove('is-reviewing');
    for (const button of document.querySelectorAll(`[data-terms-panel-id="${panel.id}"]`)) {
      if (button.dataset.termsLabel === undefined) continue;
      button.innerHTML = button.dataset.termsLabel;
      delete button.dataset.termsLabel;
    }
  };

  /**
   * Plays the panel's exit animation before hiding it, mirroring the way it opens.
   * Reopening mid-exit clears `is-closing`, which cancels the pending hide.
   */
  const closePanel = (panel) => {
    if (panel.classList.contains('is-closing')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      hidePanel(panel);
      return;
    }

    panel.classList.add('is-closing');
    const animations = panel.getAnimations();
    const finish = () => {
      if (panel.classList.contains('is-closing')) hidePanel(panel);
    };
    const fallback = window.setTimeout(finish, 220);
    Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
      window.clearTimeout(fallback);
      finish();
    });
  };

  const applyState = () => {
    getDialog('[data-terms-warning]');

    const accepted = isAccepted();

    // Covers consent given elsewhere (another tab) and a page restored from the
    // back-forward cache with the panel still open. A panel opened just to re-read the
    // texts after consent stays open.
    if (accepted) {
      for (const panel of document.querySelectorAll('[data-terms-panel][data-terms-accepting]:not([hidden])')) {
        closePanel(panel);
      }
    }

    for (const checkbox of document.querySelectorAll(CHECKBOX_SELECTOR)) {
      checkbox.checked = accepted;
    }

    for (const button of document.querySelectorAll(BUTTON_SELECTOR)) {
      const shouldDisable = button.dataset.cartEmpty === 'true';

      // Only write when it actually changes: the MutationObserver below watches
      // the same subtree and an unconditional write would loop forever.
      if (button.disabled !== shouldDisable) button.disabled = shouldDisable;
    }
  };

  document.addEventListener('change', (event) => {
    const checkbox = event.target.closest?.(CHECKBOX_SELECTOR);
    if (!checkbox) return;

    persist(checkbox.checked);
    applyState();
  });

  /**
   * Writes the consent onto the cart as an attribute, which Shopify copies onto the
   * order, so every order carries proof that the shopper accepted the preliminary
   * information form and the distance sales agreement, and when.
   */
  const recordConsent = () => {
    const timestamp = new Date().toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' });
    const url = `${window.routes?.cart_update_url ?? '/cart/update'}.js`;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: {
          [CONSENT_ATTRIBUTE]: `Ön Bilgilendirme Formu ve Mesafeli Satış Sözleşmesi onaylandı (${timestamp})`,
        },
      }),
    });
  };

  // Set while the consent is being saved, then while the re-dispatched submit passes
  // through the guard, so a double press cannot start a second save.
  let proceeding = false;

  /**
   * Saves the consent before letting checkout continue. The original navigation is
   * held back and replayed with the same button once the save settles; a failed save
   * still proceeds, because blocking a paying customer is worse than a missing record.
   */
  const proceedToCheckout = (event, button) => {
    if (proceeding) {
      if (button.dataset.termsReplaying !== undefined) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    proceeding = true;
    button.setAttribute('aria-busy', 'true');

    recordConsent()
      .catch(() => {})
      .finally(() => {
        button.removeAttribute('aria-busy');
        button.dataset.termsReplaying = '';
        try {
          button.form?.requestSubmit(button);
        } finally {
          delete button.dataset.termsReplaying;
          proceeding = false;
        }
      });
  };

  const guardCheckout = (event, button) => {
    if (isAccepted()) {
      // The click and the submit it causes both pass through here; recording once on
      // the submit covers mouse, keyboard and implicit submits alike.
      if (event.type === 'submit') proceedToCheckout(event, button);
      return;
    }

    const panel = getPanel(button);
    // While the agreement is open the button reads as the accept action. Only a real
    // press counts as consent: an implicit form submit (Enter in a quantity field)
    // must not accept on the shopper's behalf.
    if (panel && !panel.hidden && event.type === 'click') {
      persist(true);
      applyState();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (panel) {
      if (panel.hidden) openPanel(panel, button);
      return;
    }

    const dialog = getDialog('[data-terms-warning]');
    if (dialog && !dialog.open) dialog.showModal();
  };

  const closeWarning = (dialog) => {
    if (!dialog?.open || dialog.classList.contains('is-closing')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      dialog.close();
      return;
    }

    dialog.classList.add('is-closing');
    // Keep the dialog in the top layer until both it and its backdrop fade out.
    const animations = dialog.getAnimations({ subtree: true });
    const finish = () => {
      if (!dialog.classList.contains('is-closing')) return;
      dialog.close();
      dialog.classList.remove('is-closing');
    };
    const fallback = window.setTimeout(finish, 220);
    Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
      window.clearTimeout(fallback);
      finish();
    });
  };

  document.addEventListener('cancel', (event) => {
    if (!event.target.matches?.('[data-terms-warning]')) return;
    event.preventDefault();
    closeWarning(event.target);
  }, true);

  let backdropPointerDown = false;
  const isWarningBackdrop = (event) => {
    if (!event.target.matches?.('[data-terms-warning]')) return false;
    const rect = event.target.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right ||
      event.clientY < rect.top || event.clientY > rect.bottom;
  };
  document.addEventListener('pointerdown', (event) => {
    backdropPointerDown = isWarningBackdrop(event);
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.(BUTTON_SELECTOR);
    if (button) guardCheckout(event, button);
  }, true);

  document.addEventListener('submit', (event) => {
    const button = event.submitter?.matches(BUTTON_SELECTOR)
      ? event.submitter
      : !event.submitter &&
        [...document.querySelectorAll(BUTTON_SELECTOR)].find((candidate) => candidate.form === event.target);
    if (button) guardCheckout(event, button);
  }, true);

  document.addEventListener('click', (event) => {
    const clickedBackdrop = backdropPointerDown && isWarningBackdrop(event);
    backdropPointerDown = false;
    if (clickedBackdrop) {
      closeWarning(event.target);
      return;
    }
    const dismiss = event.target.closest?.('[data-terms-warning-close]');
    if (dismiss) {
      closeWarning(dismiss.closest('dialog'));
      return;
    }
    const opener = event.target.closest?.('[data-terms-dialog-open]');
    if (opener) {
      // The trigger sits inside the checkbox label, so stop the click from toggling it.
      event.preventDefault();
      const panel = opener.closest('[data-terms-gate]')?.querySelector('[data-terms-panel]');
      const button = panel && document.querySelector(`[data-terms-panel-id="${panel.id}"]`);
      if (panel && button) openPanel(panel, button, opener.dataset.termsDialogOpen);
      return;
    }

    const tab = event.target.closest?.('[data-terms-tab]');
    if (tab) {
      const panel = tab.closest('[data-terms-panel]');
      if (panel) selectTab(panel, tab.dataset.termsTab);
      return;
    }

    const panelClose = event.target.closest?.('[data-terms-panel-close]');
    if (panelClose) {
      const panel = panelClose.closest('[data-terms-panel]');
      if (panel) closePanel(panel);
      return;
    }

    const scrim = event.target.closest?.('[data-terms-scrim]');
    if (scrim) {
      for (const panel of scrim.parentElement.querySelectorAll('[data-terms-panel]:not([hidden])')) closePanel(panel);
      return;
    }
  });

  // Arrow keys move between tabs, as the WAI-ARIA tabs pattern expects.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const current = event.target.closest?.('[data-terms-tab]');
    const panel = current?.closest('[data-terms-panel]');
    if (!panel) return;

    const tabs = [...panel.querySelectorAll('[data-terms-tab]')];
    const step = event.key === 'ArrowRight' ? 1 : -1;
    const next = tabs[(tabs.indexOf(current) + step + tabs.length) % tabs.length];
    event.preventDefault();
    selectTab(panel, next.dataset.termsTab)?.focus();
  });

  document.addEventListener('DOMContentLoaded', applyState);

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY && event.key !== null) return;
    sessionAccepted = undefined;
    applyState();
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, applyState);
  }

  // Covers re-renders that do not broadcast cartUpdate, e.g. the cart drawer
  // opening straight after an add-to-cart response. applyState() moves and removes
  // dialog nodes itself, so the observer is paused around it to avoid re-entry.
  const observer = new MutationObserver(() => {
    observer.disconnect();
    applyState();
    observer.observe(document.body, { childList: true, subtree: true });
  });

  observer.observe(document.body, { childList: true, subtree: true });

  applyState();
})();
